import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Button,
  PrimaryButton,
  SecondaryButton,
} from '../../../../components';
import { useHistory } from 'react-router-dom';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import { UilShoppingCart } from '@iconscout/react-unicons';
import css from './TopbarDesktop.module.css';
import LinksMenu from './CustomLinksMenu/LinksMenu';
import TopbarDynamicSearch from '../TopbarDynamicSearch/TopbarDynamicSearch';
import MouseOverMenu from '../../../../components/MouseOverMenu/MouseOverMenu';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

export const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = (page) => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="NewListingPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('NewListingPage'))}
            name="NewListingPage"
          >
            <span className={css.menuItemBorder} />
            <span>Upload your scan</span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <span>Manage your scans</span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: 'sales' }}
          >
            <span className={css.menuItemBorder} />
            <span>Inbox</span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        {/* <MenuItem key="AccountPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountPage'))}
            name="AccountPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem> */}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = (props) => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    hideSearch,
    showSearchForm,
  } = props;
  const history = useHistory();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <div className={css.sectionLeft}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <MouseOverMenu
          label="AI"
          menuItems={[
            {
              label: 'Keyword Generation',
              path: '/terms-of-use',
            },
            {
              label: 'Scan Descriptor',
              path: '/terms-of-use',
            },
          ]}
        />

        <MouseOverMenu
          label="Sell"
          menuItems={[
            {
              label: 'Become a content creator',
              path: '/l/new',
            },
            {
              label: 'Guidelines',
              path: '/guidelines',
            },
            {
              label: 'Upload',
              path: '/l/new',
            },
          ]}
        />

        <NamedLink className={css.linkTo} name={'SearchPage'}>
          All
        </NamedLink>
        <NamedLink className={css.linkTo} name={'SearchPage'} to={{ search: 'sort=createdAt' }}>
          New
        </NamedLink>
        {/* <div className={css.shoppingCartWrapper}>
          <UilShoppingCart />
        </div> */}
      </div>
      <div className={css.sectionRight}>
        {profileMenuMaybe || (
          <>
            <SecondaryButton className={css.authButton} onClick={() => history.push('/login')}>
              Sign in
            </SecondaryButton>

            <Button className={css.authButton} onClick={() => history.push('/signup')}>
              Sign up
            </Button>
          </>
          // <LinksMenu
          //   id="linksMenu"
          //   currentPage={currentPage}
          //   label={'Account'}
          //   links={[
          //     {
          //       type: 'internal',
          //       group: 'secondary',
          //       text: 'Log in',
          //       href: '/login',
          //       route: {
          //         name: 'LoginPage',
          //         params: {},
          //         to: {
          //           search: '',
          //           hash: '',
          //         },
          //       },
          //     },
          //     {
          //       type: 'internal',
          //       group: 'secondary',
          //       text: 'Create',
          //       href: '/signup',
          //       route: {
          //         name: 'SignupPage',
          //         params: {},
          //         to: {
          //           search: '',
          //           hash: '',
          //         },
          //       },
          //     },
          //   ]}
          //   intl={intl}
          // />
        )}
      </div>

      {/* <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
        desktopInputRoot={css.topbarSearchWithLeftPadding}
        onSubmit={() => {}} //{onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      /> */}
      {/* <TopbarDynamicSearch hideSearch={hideSearch} /> */}

      {/* <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
      />

      {inboxLinkMaybe}
      {profileMenuMaybe}
      {signupLinkMaybe}
      {loginLinkMaybe} */}
    </nav>
  );
};

export default TopbarDesktop;
